import { useCallback } from 'react'
import { useSnackbar } from '@ui/snackbar'
import { useAppStore } from '@stores'

/** The browser's notification permission, `unsupported` without the Notification API. */
export const getNotificationPermission = (): NotificationPermission | 'unsupported' =>
  typeof Notification === 'undefined' ? 'unsupported' : Notification.permission

/**
 * Turns the app's notifications on: asks the browser for permission and keeps
 * the setting on only when it is granted. Resolves to the permission the
 * browser answered with.
 */
const useEnableNotifications = () => {
  const { enqueueSnackbar } = useSnackbar()
  const setShowNotifications = useAppStore(state => state.setShowNotifications)

  const enableNotifications = useCallback(async (): Promise<
    NotificationPermission | 'unsupported'
  > => {
    if (getNotificationPermission() === 'unsupported') {
      setShowNotifications(false)
      enqueueSnackbar('Dieser Browser unterstützt keine Benachrichtigungen', { variant: 'error' })
      return 'unsupported'
    }
    setShowNotifications(true)
    try {
      const result = await Notification.requestPermission()
      if (result !== 'granted') {
        setShowNotifications(false)
        enqueueSnackbar('Keine Erlaubnis zum Senden von Benachrichtigungen erteilt', {
          variant: 'error',
        })
      }
      return result
    } catch {
      setShowNotifications(false)
      enqueueSnackbar('Fehler bei der Anfrage zum Senden von Benachrichtigungen', {
        variant: 'error',
      })
      return getNotificationPermission()
    }
  }, [enqueueSnackbar, setShowNotifications])

  return { enableNotifications }
}

export default useEnableNotifications
